import moment from "moment";
import { isFullTimeJob } from "../constants/lov/professions";

const MAX_DESCRIPTION_LENGTH = 400;

export const buildJobShareUrl = (jobUuid) =>
  `https://hiresupermedic.com/job/${jobUuid}`;

/**
 * Mirrors the detail screen: "Wednesday, October 8th 2025, 9:00 AM ~ 5:00 PM"
 * for a same day shift, "October 8th, 9:00 AM ~ October 9th, 5:00 PM" when it
 * spans days. Full-time postings only carry an expiry date, so they drop the time.
 */
const formatSchedule = (start, end, withTime) => {
  if (!start) return null;

  const startMoment = moment(start);
  const endMoment = end ? moment(end) : null;

  if (!endMoment || startMoment.format("DD") === endMoment.format("DD")) {
    const date = startMoment.format("dddd, MMMM Do YYYY");
    if (!withTime) return date;
    return endMoment
      ? `${date}, ${startMoment.format("h:mm A")} ~ ${endMoment.format("h:mm A")}`
      : `${date}, ${startMoment.format("h:mm A")}`;
  }

  if (!withTime) {
    return `${startMoment.format("MMMM Do YYYY")} ~ ${endMoment.format(
      "MMMM Do YYYY"
    )}`;
  }

  return `${startMoment.format("MMMM Do, h:mm A")} ~ ${endMoment.format(
    "MMMM Do, h:mm A"
  )}`;
};

const formatLocation = (clinic) =>
  [
    clinic?.address,
    clinic?.city,
    [clinic?.postcode, clinic?.state].filter(Boolean).join(" "),
  ]
    .map((part) => part?.toString().trim())
    .filter(Boolean)
    .join(", ");

const truncate = (text) => {
  const value = text?.toString().trim();
  if (!value) return null;
  return value.length > MAX_DESCRIPTION_LENGTH
    ? `${value.slice(0, MAX_DESCRIPTION_LENGTH).trimEnd()}...`
    : value;
};

/**
 * Builds the shareable job blurb sent to WhatsApp/Telegram/Viber/email/clipboard.
 * Every section is optional so a partially loaded job still produces a sane message.
 * Pass includeLink: false for channels that carry the link separately (Telegram),
 * or url to override the candidate-facing link (the web app shares the page URL).
 */
export const buildJobShareMessage = (
  job,
  jobUuid,
  { includeLink = true, url } = {}
) => {
  const uuid = job?.uuid || jobUuid;
  const clinic = job?.clinic;
  const title = job?.title;
  const isFullTime = isFullTimeJob(job?.type);
  const schedule = formatSchedule(
    job?.start_timestamp,
    job?.end_timestamp,
    !isFullTime
  );
  const location = formatLocation(clinic);
  const description = truncate(job?.description);
  const contact = clinic?.phone_number;
  const contactPerson = job?.contact_person?.toString().trim();
  const dateLabel = isFullTime ? "Expires" : "Date & Time";

  const lines = ["Check out this healthcare job opportunity!", ""];

  if (title) lines.push(`Job: ${title}`);
  if (clinic?.name) lines.push(`Clinic: ${clinic.name}`);
  if (schedule) lines.push(`${dateLabel}: ${schedule}`);
  if (location) lines.push(`Location: ${location}`);
  if (contact) lines.push(`Contact No: ${contact}`);
  if (contactPerson) lines.push(`Person in-charge: ${contactPerson}`);

  if (description) {
    lines.push("", "Job Description:", description);
  }

  const link = url || (uuid ? buildJobShareUrl(uuid) : null);
  if (includeLink && link) {
    lines.push("", `Apply here: ${link}`);
  }

  return lines.join("\n");
};
